import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { toast } from "react-toastify";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import { COLORS } from "../../assets/theme";
import {
  BOOKING_HORIZON_DAYS,
  getClassSessions,
  updateClass,
} from "../../services/ClassesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { formatTableDate } from "../../utils/tableDateFormat";
import {
  ClassDefinition,
  UpdateClassPayload,
  UpdateClassResult,
} from "./types";
import {
  formatTime,
  isSessionCancelled,
  toDateInputValue,
} from "./classUtils";

interface DeactivateClassDialogProps {
  open: boolean;
  classItem: ClassDefinition | null;
  /**
   * The full edit payload the form was submitting, `active: false` included —
   * sent as one PATCH so the other edits in the same save aren't dropped.
   */
  payload: UpdateClassPayload | null;
  onClose: () => void;
  onDeactivated: () => void;
}

interface Impact {
  sessions: number;
  sessionsWithBookings: number;
  bookings: number;
  firstDate?: string;
  lastDate?: string;
}

/**
 * Deactivating a class used to be a free toggle. It now cancels every future
 * materialized session — refunding package credits, returning seats, voiding
 * invoices and messaging every attendee — so it gets a confirmation with the
 * blast radius on it rather than going straight through on a dropdown change.
 *
 * The count is worked out up front by reading the session window, because
 * "this will cancel some sessions" is not something staff can weigh. If that
 * read fails the dialog still opens, just with generic wording — a failed
 * count must never be the reason a destructive action goes unguarded.
 */
const DeactivateClassDialog: React.FC<DeactivateClassDialogProps> = ({
  open,
  classItem,
  payload,
  onClose,
  onDeactivated,
}) => {
  const [impact, setImpact] = React.useState<Impact | null>(null);
  const [counting, setCounting] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState<UpdateClassResult | null>(null);

  const classId = classItem?.id ? String(classItem.id) : "";

  React.useEffect(() => {
    if (!open || !classId) return;
    let cancelled = false;
    setResult(null);
    setImpact(null);

    // Only materialized sessions get cancelled — a projected date has no row to
    // cancel and simply stops being projected. So the window is read out to the
    // booking horizon (a booking can materialize a session up to a year out)
    // and everything still virtual is filtered back out.
    const countImpact = async () => {
      setCounting(true);
      try {
        const today = new Date();
        const horizon = new Date(today.getTime());
        horizon.setDate(horizon.getDate() + BOOKING_HORIZON_DAYS);

        const { sessions } = await getClassSessions(classId, {
          start: toDateInputValue(today),
          end: toDateInputValue(horizon),
        });

        const now = Date.now();
        const doomed = sessions.filter((session) => {
          if (session.is_virtual === true) return false;
          if (isSessionCancelled(session.status)) return false;
          const startsAt = new Date(String(session.start_at)).getTime();
          return Number.isFinite(startsAt) && startsAt > now;
        });

        if (cancelled) return;
        setImpact({
          sessions: doomed.length,
          sessionsWithBookings: doomed.filter((s) => (s.booked_count ?? 0) > 0)
            .length,
          bookings: doomed.reduce((sum, s) => sum + (s.booked_count ?? 0), 0),
          firstDate: doomed[0]?.start_at,
          lastDate: doomed[doomed.length - 1]?.start_at,
        });
      } catch {
        // Generic wording rather than a blocked dialog.
        if (!cancelled) setImpact(null);
      } finally {
        if (!cancelled) setCounting(false);
      }
    };

    void countImpact();
    return () => {
      cancelled = true;
    };
  }, [open, classId]);

  const handleConfirm = async () => {
    if (!classId || !payload) return;
    setSubmitting(true);
    try {
      const response = await updateClass(classId, payload);
      setResult(response);
      toast.success(`"${classItem?.name || "Class"}" deactivated.`);
      onDeactivated();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to deactivate the class."));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setResult(null);
    onClose();
  };

  const cancelledCount = result?.cancelled_sessions.length ?? 0;
  const notifiedCount = result?.clients_to_notify.length ?? 0;

  return (
    <CustomDialog
      open={open}
      onClose={() => {
        if (!submitting) close();
      }}
      paperSx={{ minWidth: { sm: 540 }, maxWidth: 700 }}
      title={
        result
          ? "Class deactivated"
          : `Deactivate ${classItem?.name || "this class"}?`
      }
      actions={
        result ? (
          <CustomButton
            label="Done"
            variant="contained"
            bgColor={COLORS.MainColor}
            textColor={COLORS.WhiteText}
            onClick={close}
          />
        ) : (
          <>
            <CustomButton
              label="Keep active"
              variant="outlined"
              onClick={close}
              disabled={submitting}
            />
            <CustomButton
              label={
                impact && impact.sessions > 0
                  ? `Cancel ${impact.sessions} session${
                      impact.sessions === 1 ? "" : "s"
                    } & deactivate`
                  : "Deactivate"
              }
              variant="contained"
              bgColor="#d32f2f"
              textColor="#fff"
              loading={submitting}
              disabled={submitting || counting}
              onClick={handleConfirm}
            />
          </>
        )
      }
    >
      {result ? (
        <>
          <Typography sx={{ fontSize: 14, mb: 1.5 }}>
            {cancelledCount === 0
              ? "No upcoming sessions had been created yet, so nothing was cancelled."
              : `${cancelledCount} upcoming session${
                  cancelledCount === 1 ? "" : "s"
                } cancelled. Package credits were refunded, seats returned and invoices voided.`}
          </Typography>

          {notifiedCount > 0 && (
            <Box
              sx={{
                border: "1px solid #d6ecd8",
                bgcolor: "#f5fbf6",
                borderRadius: 2,
                p: 1.5,
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#2e7d32" }}>
                {`${notifiedCount} client${
                  notifiedCount === 1 ? " was" : "s were"
                } notified`}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#4a6b4d", mt: 0.5 }}>
                Sent automatically when the sessions were cancelled — there is
                nothing for you to send.
              </Typography>
              <Box sx={{ mt: 1 }}>
                {result?.clients_to_notify.slice(0, 8).map((client, index) => (
                  <Typography
                    key={String(client.id || index)}
                    sx={{ fontSize: 13, color: "#4a6b4d" }}
                  >
                    {`• ${client.full_name || "Client"}${
                      client.phone ? ` · ${client.phone}` : ""
                    }`}
                  </Typography>
                ))}
                {notifiedCount > 8 && (
                  <Typography sx={{ fontSize: 12, color: "#7a8f7c" }}>
                    {`+ ${notifiedCount - 8} more`}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          <Typography sx={{ fontSize: 13, color: "#777", mt: 2 }}>
            Switching the class back to Active frees any session nobody had
            booked. The ones people were refunded for stay cancelled.
          </Typography>
        </>
      ) : counting ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={26} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              border: "1px solid #f2d0d0",
              bgcolor: "#fdf4f4",
              borderRadius: 2,
              p: 1.75,
              mb: 2,
            }}
          >
            <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: "#8a3a3a" }}>
              {impact
                ? impact.sessions === 0
                  ? "No upcoming sessions to cancel."
                  : `This cancels ${impact.sessions} upcoming session${
                      impact.sessions === 1 ? "" : "s"
                    }.`
                : "This will cancel all upcoming sessions and refund anyone booked."}
            </Typography>

            {impact && impact.sessions > 0 && (
              <Typography sx={{ fontSize: 13.5, color: "#8a3a3a", mt: 0.75 }}>
                {impact.bookings === 0
                  ? "Nobody is booked on any of them, so no refunds are due."
                  : `${impact.bookings} booking${
                      impact.bookings === 1 ? "" : "s"
                    } across ${impact.sessionsWithBookings} of them will be refunded — package credits returned or invoices voided — and those clients are messaged automatically.`}
              </Typography>
            )}

            {impact && impact.sessions > 0 && impact.firstDate && (
              <Typography sx={{ fontSize: 13, color: "#a05a5a", mt: 0.75 }}>
                {`${formatTableDate(impact.firstDate)} · ${formatTime(
                  impact.firstDate
                )}${
                  impact.lastDate && impact.lastDate !== impact.firstDate
                    ? ` — ${formatTableDate(impact.lastDate)}`
                    : ""
                }`}
              </Typography>
            )}
          </Box>

          <Typography sx={{ fontSize: 13.5, color: "#555" }}>
            Deactivating is reversible — switch the class back to Active and it
            starts projecting sessions again. Only the ones nobody had booked
            come back.
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#777", mt: 1 }}>
            To stop the class from a specific date instead, leaving that day's
            sessions to run, use End rather than deactivating.
          </Typography>
        </>
      )}
    </CustomDialog>
  );
};

export default DeactivateClassDialog;
